'use client';

import { c as _c } from "react/compiler-runtime";
import { Calendar, Gift, Hand, Heart, Settings } from '@byway/icons';
import { useUser } from '@clerk/nextjs';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { useSignOut } from '~/hooks/useSignOut';
import { Link, usePathname } from '~/i18n/navigation';
import styles from './AccountMenu.module.css';
import { UserAvatar } from './UserAvatar';
import { useAccountTier } from './useAccountTier';
import { getUserDisplayName } from './userIdentity';
type AccountMenuProps = {
  readonly onNavigate: () => void;
  /**
   * The panel leads with the profile block and plain text rows, per the design; the desktop
   * popover is a compact icon menu with no profile block, since the badge it hangs off already
   * identifies the user.
   */
  readonly variant: 'panel' | 'popover';
};
export const AccountMenu = t0 => {
  const $ = _c(44);
  const {
    onNavigate,
    variant
  } = t0;
  const {
    user
  } = useUser();
  const t = useTranslations("account");
  const signOut = useSignOut();
  const pathname = usePathname();
  const isPanel = variant === "panel";
  const tierLabel = useAccountTier(isPanel);
  if (!user) {
    return null;
  }
  const isReferEnabled = process.env.NEXT_PUBLIC_FF_REFER_ENABLED === "true";
  const isRewardsEnabled = process.env.NEXT_PUBLIC_FF_REWARDS_ENABLED === "true";
  let itemClass;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== isPanel || $[1] !== onNavigate || $[2] !== pathname || $[3] !== t || $[4] !== tierLabel || $[5] !== user) {
    const items = [{
      href: "/account",
      icon: Heart,
      label: t("menu_saved_trips"),
      testId: "saved-trips"
    }, {
      href: "/account/booked",
      icon: Calendar,
      label: t("menu_my_trips"),
      testId: "my-trips"
    }, ...(isReferEnabled ? [{
      href: "/account/refer",
      icon: Hand,
      label: t("menu_refer"),
      testId: "refer"
    }] : []), ...(isRewardsEnabled ? [{
      href: "/account/rewards",
      icon: Gift,
      label: t("menu_rewards"),
      testId: "rewards"
    }] : []), {
      href: "/account/settings",
      icon: Settings,
      label: t("menu_settings"),
      testId: "settings"
    }];
    let t6;
    if ($[12] !== pathname) {
      t6 = href => href === "/account" ? pathname === href : pathname.startsWith(href);
      $[12] = pathname;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    const isCurrent = t6;
    itemClass = clsx(styles.item, isPanel ? styles.itemPanel : styles.itemPopover);
    t4 = styles.menu;
    if ($[14] !== isPanel || $[15] !== onNavigate || $[16] !== tierLabel || $[17] !== user) {
      t5 = isPanel && <Link href="/account" className={styles.profile} onClick={onNavigate} data-test-id="user-menu-profile"><span className={styles.avatar}><UserAvatar user={user} /></span><span className={styles.identity}><span className={styles.name}>{getUserDisplayName(user)}</span>{tierLabel && <span className={styles.tier}>{tierLabel}</span>}</span></Link>;
      $[14] = isPanel;
      $[15] = onNavigate;
      $[16] = tierLabel;
      $[17] = user;
      $[18] = t5;
    } else {
      t5 = $[18];
    }
    const t7 = isPanel && styles.navBelowProfile;
    if ($[19] !== t7) {
      t1 = clsx(styles.nav, t7);
      $[19] = t7;
      $[20] = t1;
    } else {
      t1 = $[20];
    }
    if ($[21] !== t) {
      t2 = t("menu_label");
      $[21] = t;
      $[22] = t2;
    } else {
      t2 = $[22];
    }
    t3 = items.map(t8 => {
      const {
        href: href_0,
        icon: Icon,
        label,
        testId
      } = t8;
      const current = isCurrent(href_0);
      return <Link key={href_0} href={href_0} className={clsx(itemClass, current && styles.itemCurrent)} aria-current={current ? "page" : undefined} onClick={onNavigate} data-test-id={`user-menu-${testId}`}>{!isPanel && <Icon decorative={true} className={styles.icon} />}{label}</Link>;
    });
    $[0] = isPanel;
    $[1] = onNavigate;
    $[2] = pathname;
    $[3] = t;
    $[4] = tierLabel;
    $[5] = user;
    $[6] = itemClass;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    itemClass = $[6];
    t1 = $[7];
    t2 = $[8];
    t3 = $[9];
    t4 = $[10];
    t5 = $[11];
  }
  let t6;
  if ($[23] !== isPanel) {
    t6 = isPanel ? null : <hr className={styles.divider} />;
    $[23] = isPanel;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  let t7;
  if ($[25] !== onNavigate || $[26] !== signOut) {
    t7 = () => {
      onNavigate();
      signOut();
    };
    $[25] = onNavigate;
    $[26] = signOut;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  let t8;
  if ($[28] !== t) {
    t8 = t("menu_sign_out");
    $[28] = t;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  let t9;
  if ($[30] !== itemClass || $[31] !== t7 || $[32] !== t8) {
    t9 = <button type="button" className={itemClass} onClick={t7} data-test-id="user-menu-sign-out">{t8}</button>;
    $[30] = itemClass;
    $[31] = t7;
    $[32] = t8;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  let t10;
  if ($[34] !== t1 || $[35] !== t2 || $[36] !== t3 || $[37] !== t6 || $[38] !== t9) {
    t10 = <nav className={t1} aria-label={t2}>{t3}{t6}{t9}</nav>;
    $[34] = t1;
    $[35] = t2;
    $[36] = t3;
    $[37] = t6;
    $[38] = t9;
    $[39] = t10;
  } else {
    t10 = $[39];
  }
  let t11;
  if ($[40] !== t10 || $[41] !== t4 || $[42] !== t5) {
    t11 = <div className={t4}>{t5}{t10}</div>;
    $[40] = t10;
    $[41] = t4;
    $[42] = t5;
    $[43] = t11;
  } else {
    t11 = $[43];
  }
  return t11;
};